/**
 * usePayGBotContext Hook
 * ======================
 * Combined hook for accessing both business context and workflow progress.
 * Provides a unified interface for components to access global memory.
 */
import { useEffect } from 'react';
import { useBusinessContext } from './businessContext';
import { useWorkflowProgress, FLOW_STEPS } from './workflowProgress';
import { getUser } from '@/utils/auth';

export { FLOW_STEPS };

/**
 * Hook to access PayGBot's global memory (business context + workflow progress)
 */
export function usePayGBotContext() {
  const businessContext = useBusinessContext();
  const workflowProgress = useWorkflowProgress();
  
  // Sync from backend on mount if user is logged in
  useEffect(() => {
    const user = getUser();
    if (user) {
      // Only sync if not recently synced (within last 5 minutes)
      const fiveMinutesAgo = Date.now() - 5 * 60 * 1000;
      
      if (!businessContext.lastSyncedAt || new Date(businessContext.lastSyncedAt).getTime() < fiveMinutesAgo) {
        businessContext.syncFromBackend();
      }
      
      if (!workflowProgress.lastSyncedAt || new Date(workflowProgress.lastSyncedAt).getTime() < fiveMinutesAgo) {
        workflowProgress.syncFromBackend();
      }
    }
  }, []);
  
  return {
    // Business context (who they are)
    business: {
      niche: businessContext.niche,
      businessType: businessContext.businessType,
      targetAudience: businessContext.targetAudience,
      platform: businessContext.primaryPlatform,
      brandVoice: businessContext.brandVoice,
      contentTone: businessContext.contentTone,
      offerFocus: businessContext.offerFocus,
      contentStyle: businessContext.contentStyle,
      isConfigured: businessContext.isConfigured(),
      isLoading: businessContext.isLoading
    },
    
    // Workflow progress (where they are)
    workflow: {
      incomePathCompleted: workflowProgress.incomePathCompleted,
      contentIdeasCompleted: workflowProgress.contentIdeasCompleted,
      weeklyPlanCompleted: workflowProgress.weeklyPlanCompleted,
      videosCreated: workflowProgress.videosCreated,
      todaysPostReady: workflowProgress.todaysPostReady,
      currentStep: workflowProgress.currentStep,
      lastCompletedStep: workflowProgress.lastCompletedStep,
      progressPercentage: workflowProgress.getProgressPercentage(),
      nextStep: workflowProgress.getNextStep(),
      nextAction: workflowProgress.getNextAction(),
      isLoading: workflowProgress.isLoading
    },
    
    // Actions
    actions: {
      // Business context actions
      updateBusinessContext: businessContext.updateContext,
      setBusinessContext: businessContext.setFullContext,
      saveBusinessContext: businessContext.saveToBackend,
      syncBusinessContext: businessContext.syncFromBackend,
      getContentContext: businessContext.getContentContext,
      getContextForAPI: businessContext.getContextForAPI,
      
      // Workflow progress actions
      completeIncomePath: workflowProgress.completeIncomePath,
      completeContentIdeas: workflowProgress.completeContentIdeas,
      completeWeeklyPlan: workflowProgress.completeWeeklyPlan,
      completeVideoCreation: workflowProgress.completeVideoCreation,
      markTodaysPostReady: workflowProgress.markTodaysPostReady,
      isStepUnlocked: workflowProgress.isStepUnlocked,
      getStatusSummary: workflowProgress.getStatusSummary,
      
      // Reset actions
      resetTodaysProgress: workflowProgress.resetTodaysProgress,
      resetAllProgress: workflowProgress.resetAllProgress,
      resetBusinessContext: businessContext.resetContext
    },
    
    // Helper to get pre-filled form data for any tool
    getFormDefaults: () => ({
      niche: businessContext.niche,
      target_audience: businessContext.targetAudience,
      audience: businessContext.targetAudience,
      platform: businessContext.primaryPlatform,
      tone: businessContext.contentTone,
      style: businessContext.contentStyle,
      brand_voice: businessContext.brandVoice,
      offer_focus: businessContext.offerFocus
    })
  };
}

/**
 * Hook specifically for form auto-fill (lighter weight)
 */
export function useFormDefaults() {
  const { niche, targetAudience, primaryPlatform, contentTone, contentStyle, brandVoice, offerFocus } = useBusinessContext();
  
  return {
    niche,
    target_audience: targetAudience,
    audience: targetAudience,
    platform: primaryPlatform,
    tone: contentTone,
    style: contentStyle,
    brand_voice: brandVoice,
    offer_focus: offerFocus
  };
}

/**
 * Hook for workflow routing decisions
 */
export function useWorkflowRouting() {
  const progress = useWorkflowProgress();
  
  return {
    shouldShowIncomePath: !progress.incomePathCompleted,
    shouldShowContentIdeas: progress.incomePathCompleted && !progress.contentIdeasCompleted,
    shouldShowWeeklyPlan: progress.contentIdeasCompleted && !progress.weeklyPlanCompleted,
    shouldShowVideoCreation: progress.weeklyPlanCompleted && !progress.videosCreated,
    shouldShowTodaysPost: progress.videosCreated,
    
    nextStep: progress.getNextStep(),
    nextAction: progress.getNextAction(),
    isStepUnlocked: progress.isStepUnlocked,
    progressPercentage: progress.getProgressPercentage()
  };
}

export default usePayGBotContext;
